import {omit} from "@pg-mono/nodash";
import {appendQueryString} from "@pg-mono/request";

import {addRegionSlugToQuery} from "../../offer/helpers/friendly_offer_list/add_region_slug_to_query";
import {divideQueryIntoFriendlyAndRest, offerUrlBuilder} from "../../offer/helpers/friendly_offer_list/offer_url_builder";
import {SearchSort, SearchSortString} from "../../offer/list/constants/SearchSort";
import {getCountryId, getCountryName} from "../../offer/list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {OfferType} from "../../offer/types/OfferType";
import {Country} from "../../region/types/Country";
import {ILatestQuery} from "../../utils/latest_query";
import {ISearchInputValue} from "../components/ISearchInputValue";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {getStringQueryValuesFromFormValues} from "./get_string_values_from_form_values";
import {validateQueryValues} from "./validate_query_values";

export interface IGetSearchUrlWithFiltersProps {
    singleRegionFriendlyLink: (params: {friendlySlug: string; offerListSubFilterSlug?: string}) => string;
    abroadListFriendlyLink: (params: {friendlySlug: string; country: string; abroadRegionSlug?: string}) => string;
    multiRegionLink: () => string;
    latestQuery: Pick<ILatestQuery, "is_luxury" | "price_lower_than_average" | "sort" | "country" | "distance" | "region" | "page">;
    searchFormValues: SearchFormValuesType;
    additionalParams?: {
        sort?: number | string;
        distance?: number | string;
        restored_filters?: string;
    };
    userQueryParams?: {distance?: number};
}

// Logic moved from apps/rp/src/common/search/components/Search.tsx
export const getSearchUrlWithFilters = (props: IGetSearchUrlWithFiltersProps) => {
    const {singleRegionFriendlyLink, multiRegionLink, latestQuery, searchFormValues, abroadListFriendlyLink} = props;
    const searchRegionInputValue = searchFormValues["search"] as ISearchInputValue;

    let stringFormQueryValues = getStringQueryValuesFromFormValues(searchFormValues);

    // Clear `floor_choices` values if user search for house or commercial
    if (stringFormQueryValues?.floor_choices?.length !== 0 && [OfferType.HOUSE, OfferType.COMMERCIAL].includes(searchFormValues.type[0])) {
        stringFormQueryValues = {...stringFormQueryValues, floor_choices: []};
    }

    // Clear house filters values if user search for flat or commercial
    if (
        (stringFormQueryValues?.house_additional_areas?.length !== 0 ||
            stringFormQueryValues?.house_storeys?.length !== 0 ||
            stringFormQueryValues?.house_type?.length !== 0) &&
        [OfferType.FLAT, OfferType.COMMERCIAL].includes(searchFormValues.type[0])
    ) {
        stringFormQueryValues = {...stringFormQueryValues, house_additional_areas: [], house_storeys: [], house_type: []};
    }

    let newFilterValues = {...stringFormQueryValues};
    if (newFilterValues?.type && typeof newFilterValues?.type !== "string") {
        newFilterValues = omit(newFilterValues, ["type"]);
    }

    stringFormQueryValues = validateQueryValues(stringFormQueryValues);

    // Multi-region search behaviour - we skip building friendly url
    if (searchRegionInputValue.tabType === SearchTab.Regions && searchRegionInputValue.regions.length > 1) {
        return {
            url: appendQueryString(multiRegionLink(), {
                ...stringFormQueryValues,
                sort: props.additionalParams?.sort,
                ...(props.additionalParams?.restored_filters && {restored_filters: props.additionalParams.restored_filters})
            }),
            filterValues: newFilterValues
        };
    }

    let finalQuery = {...stringFormQueryValues} as Record<string, string | string[]>;

    if (searchRegionInputValue.tabType === SearchTab.Regions) {
        finalQuery = addRegionSlugToQuery(finalQuery, searchRegionInputValue.regions[0]);
    }

    if (latestQuery.is_luxury === "true") {
        finalQuery.is_luxury = "true";
    }

    if (latestQuery.price_lower_than_average) {
        finalQuery.price_lower_than_average = "true";
    }

    let currentSort: SearchSort | null = null;

    switch (latestQuery.sort) {
        case SearchSortString.PRICE:
            currentSort = SearchSort.PRICE;
            break;
        case SearchSortString.CONSTRUCTION_DATE:
            currentSort = SearchSort.CONSTRUCTION_DATE;
    }
    const [friendlyQueryValues, restQueryValues] = divideQueryIntoFriendlyAndRest(finalQuery);
    const {friendlySlug, offerListSubFilterSlug} = offerUrlBuilder({
        ...friendlyQueryValues,
        ...(currentSort && {sort: currentSort})
    });

    const isSpain = getCountryId(props.latestQuery.country) === Country.SPAIN;
    if (isSpain) {
        const sanitizedFilterValues = omit(newFilterValues, ["region", "type"]);
        const {friendlySlug} = offerUrlBuilder({
            region: "",
            type: friendlyQueryValues.type
        });

        return {
            url: appendQueryString(
                abroadListFriendlyLink({
                    friendlySlug: friendlySlug,
                    country: getCountryName(getCountryId(props.latestQuery.country)),
                    ...(friendlyQueryValues.region ? {abroadRegionSlug: friendlyQueryValues.region} : {})
                }),
                {
                    ...sanitizedFilterValues,
                    sort: props.additionalParams?.sort,
                    page: latestQuery.page,
                    ...(props.additionalParams?.restored_filters && {restored_filters: props.additionalParams.restored_filters})
                }
            ),
            filterValues: sanitizedFilterValues
        };
    }

    return {
        url: appendQueryString(singleRegionFriendlyLink({friendlySlug, offerListSubFilterSlug}), {
            ...(Array.isArray(restQueryValues.construction_end_date) &&
            restQueryValues.construction_end_date[0] === "0" &&
            !friendlyQueryValues.is_luxury &&
            !friendlyQueryValues.price_lower_than_average
                ? {...restQueryValues, construction_end_date: []}
                : restQueryValues),
            sort: props.additionalParams?.sort,
            distance: (props.userQueryParams?.distance !== undefined ? props.userQueryParams?.distance : props.additionalParams?.distance) ?? undefined,
            page: latestQuery.page,
            ...(props.additionalParams?.restored_filters && {restored_filters: props.additionalParams.restored_filters})
        }),
        filterValues: newFilterValues
    };
};
import {omit} from "@pg-mono/nodash";
import {appendQueryString} from "@pg-mono/request";

import {addRegionSlugToQuery} from "../../offer/helpers/friendly_offer_list/add_region_slug_to_query";
import {divideQueryIntoFriendlyAndRest, offerUrlBuilder} from "../../offer/helpers/friendly_offer_list/offer_url_builder";
import {SearchSort, SearchSortString} from "../../offer/list/constants/SearchSort";
import {getCountryId, getCountryName} from "../../offer/list/helpers/sub_type/get_fetch_offer_list_subfilter_query";
import {OfferType} from "../../offer/types/OfferType";
import {Country} from "../../region/types/Country";
import {ILatestQuery} from "../../utils/latest_query";
import {ISearchInputValue} from "../components/ISearchInputValue";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {SearchTab} from "../types/SearchTab";
import {getStringQueryValuesFromFormValues} from "./get_string_values_from_form_values";
import {validateQueryValues} from "./validate_query_values";

export interface IGetSearchUrlWithFiltersProps {
    singleRegionFriendlyLink: (params: {friendlySlug: string; offerListSubFilterSlug?: string}) => string;
    abroadListFriendlyLink: (params: {friendlySlug: string; country: string; abroadRegionSlug?: string}) => string;
    multiRegionLink: () => string;
    latestQuery: Pick<ILatestQuery, "is_luxury" | "price_lower_than_average" | "sort" | "country" | "distance" | "region" | "page">;
    searchFormValues: SearchFormValuesType;
    additionalParams?: {
        sort?: number | string;
        distance?: number | string;
        restored_filters?: string;
    };
    userQueryParams?: {distance?: number};
}

// Logic moved from apps/rp/src/common/search/components/Search.tsx
export const getSearchUrlWithFilters = (props: IGetSearchUrlWithFiltersProps) => {
    const {singleRegionFriendlyLink, multiRegionLink, latestQuery, searchFormValues, abroadListFriendlyLink} = props;
    const searchRegionInputValue = searchFormValues["search"] as ISearchInputValue;

    let stringFormQueryValues = getStringQueryValuesFromFormValues(searchFormValues);

    // Clear `floor_choices` values if user search for house or commercial
    if (stringFormQueryValues?.floor_choices?.length !== 0 && [OfferType.HOUSE, OfferType.COMMERCIAL].includes(searchFormValues.type[0])) {
        stringFormQueryValues = {...stringFormQueryValues, floor_choices: []};
    }

    // Clear house filters values if user search for flat or commercial
    if (
        (stringFormQueryValues?.house_additional_areas?.length !== 0 ||
            stringFormQueryValues?.house_storeys?.length !== 0 ||
            stringFormQueryValues?.house_type?.length !== 0) &&
        [OfferType.FLAT, OfferType.COMMERCIAL].includes(searchFormValues.type[0])
    ) {
        stringFormQueryValues = {...stringFormQueryValues, house_additional_areas: [], house_storeys: [], house_type: []};
    }

    let newFilterValues = {...stringFormQueryValues};
    if (newFilterValues?.type && typeof newFilterValues?.type !== "string") {
        newFilterValues = omit(newFilterValues, ["type"]);
    }

    stringFormQueryValues = validateQueryValues(stringFormQueryValues);

    // Multi-region search behaviour - we skip building friendly url
    if (searchRegionInputValue.tabType === SearchTab.Regions && searchRegionInputValue.regions.length > 1) {
        return {
            url: appendQueryString(multiRegionLink(), {
                ...stringFormQueryValues,
                sort: props.additionalParams?.sort,
                ...(props.additionalParams?.restored_filters && {restored_filters: props.additionalParams.restored_filters})
            }),
            filterValues: newFilterValues
        };
    }

    let finalQuery = {...stringFormQueryValues} as Record<string, string | string[]>;

    if (searchRegionInputValue.tabType === SearchTab.Regions) {
        finalQuery = addRegionSlugToQuery(finalQuery, searchRegionInputValue.regions[0]);
    }

    if (latestQuery.is_luxury === "true") {
        finalQuery.is_luxury = "true";
    }

    if (latestQuery.price_lower_than_average) {
        finalQuery.price_lower_than_average = "true";
    }

    let currentSort: SearchSort | null = null;

    switch (latestQuery.sort) {
        case SearchSortString.PRICE:
            currentSort = SearchSort.PRICE;
            break;
        case SearchSortString.CONSTRUCTION_DATE:
            currentSort = SearchSort.CONSTRUCTION_DATE;
    }
    const [friendlyQueryValues, restQueryValues] = divideQueryIntoFriendlyAndRest(finalQuery);
    const {friendlySlug, offerListSubFilterSlug} = offerUrlBuilder({
        ...friendlyQueryValues,
        ...(currentSort && {sort: currentSort})
    });

    const isSpain = getCountryId(props.latestQuery.country) === Country.SPAIN;
    if (isSpain) {
        const sanitizedFilterValues = omit(newFilterValues, ["region", "type"]);
        const {friendlySlug} = offerUrlBuilder({
            region: "",
            type: friendlyQueryValues.type
        });

        return {
            url: appendQueryString(
                abroadListFriendlyLink({
                    friendlySlug: friendlySlug,
                    country: getCountryName(getCountryId(props.latestQuery.country)),
                    ...(friendlyQueryValues.region ? {abroadRegionSlug: friendlyQueryValues.region} : {})
                }),
                {
                    ...sanitizedFilterValues,
                    sort: props.additionalParams?.sort,
                    page: latestQuery.page,
                    ...(props.additionalParams?.restored_filters && {restored_filters: props.additionalParams.restored_filters})
                }
            ),
            filterValues: sanitizedFilterValues
        };
    }

    return {
        url: appendQueryString(singleRegionFriendlyLink({friendlySlug, offerListSubFilterSlug}), {
            ...(Array.isArray(restQueryValues.construction_end_date) &&
            restQueryValues.construction_end_date[0] === "0" &&
            !friendlyQueryValues.is_luxury &&
            !friendlyQueryValues.price_lower_than_average
                ? {...restQueryValues, construction_end_date: []}
                : restQueryValues),
            sort: props.additionalParams?.sort,
            distance: (props.userQueryParams?.distance !== undefined ? props.userQueryParams?.distance : props.additionalParams?.distance) ?? undefined,
            page: latestQuery.page,
            ...(props.additionalParams?.restored_filters && {restored_filters: props.additionalParams.restored_filters})
        }),
        filterValues: newFilterValues
    };
};
